import { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';

import { track } from '@paradigm/analytics';
import { UNIFIED_RFQS_PAGE } from '@paradigm/analytics/src/event-names';
import { QuoteBuilderModalProvider as DrfqQuoteBuilderModalProvider } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import { QuoteBuilderModalProvider as GrfqQuoteBuilderModalProvider } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import RfqFilterProvider from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/provider';
import { RfqBuilderModalProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/modal-context';
import { UnifiedRfqServicesProvider } from '@paradigm/features/src/unified-rfqs/contexts/provider';
import { useEffectOnce } from '@paradigm/utils/src/hooks';
import { BottomTabsProvider } from '@paradigm/features/src/unified-rfqs/ui/bottom-tabs';
import { getUserID } from '@paradigm/store/auth';
import {
  INITIAL_TAB_STATE,
  TabsState,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/tabs-context';
import NotificationHandlerProvider from '@paradigm/features/src/unified-rfqs/ui/notifications/notifications-handler-context';
import SplitScreen from '#/components/SplitScreen';
import useLocalStorage from '#/hooks/local-storage';
import { MobileOpenRfqBuilderButton } from '#/components/UnifiedRfqs/rfq-builder/MobileOpenRfqBuilderButton';
import { ToastContainer } from '#/components/toasts/toast';

import RfqBuilder from '#/components/UnifiedRfqs/rfq-builder/RfqBuilder';
import DrfqQuoteBuilder from '#/components/UnifiedRfqs/drfq/quote-builder/QuoteBuilder';
import GrfqQuoteBuilder from '#/components/UnifiedRfqs/grfq/quote-builder/QuoteBuilder';
import RfqTable from '#/components/UnifiedRfqs/rfqs/RfqTable';
import UnifiedRfqActionHeader from '#/components/UnifiedRfqs/UnifiedRfqActionHeader';
import BottomPanel from '#/components/UnifiedRfqs/BottomPanel';
import { MarkPriceLegend } from '#/components/UnifiedRfqs/one-click-trade/MarkPrice';
import RfqFilterModal from '#/components/UnifiedRfqs/rfq-filters/RfqFilterModal';
import {
  toastDrfqClosed,
  toastTrade,
  toastTradeSubmitted,
  BASE_TOAST_CONTAINER_ID,
  toastDrfqOrderCanceled,
  toastWarning,
  TRADE_TOAST_CONTAINER_ID,
} from '#/components/UnifiedRfqs/toast';
import {
  UMPlatformStateProviders,
  useUMPlatformStateView,
} from '@paradigm/features/src/platform-state/contexts/providers';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import LeaderboardModal from '#/components/Leaderboard/LeaderboardModal';
import { useUnifiedMarketsPlatformStateFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { TrackingCtxProvider } from '@paradigm/features/src/analytics';
import InstrumentsProvider from '@paradigm/features/src/unified-rfqs/ui/instruments/context';
import PositionsProviders from '@paradigm/features/src/positions/contexts/providers';
import PlatformStateHandler from '../PlatformState/NotificationHandler';
import UnifiedRfqsFilterForm from './rfq-filters/UnifiedRfqsFilterForm';

export default function UnifiedRfqs() {
  useEffectOnce(() => {
    track(UNIFIED_RFQS_PAGE);
  });

  return (
    <>
      <RfqBuilder />
      <DrfqQuoteBuilder />
      <GrfqQuoteBuilder />
      <TrackingCtxProvider value="markets table">
        <RfqFilterModal formContent={<UnifiedRfqsFilterForm />} />
        <UnifiedRfqActionHeader />
        <ToastContainer containerId={BASE_TOAST_CONTAINER_ID} limit={8} />
        <ToastContainer
          containerId={TRADE_TOAST_CONTAINER_ID}
          position="top-right"
        />
        <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
          <MobileOpenRfqBuilderButton />
        </MediaQuery>
      </TrackingCtxProvider>
      <SplitScreen
        id="unified-rfq-main"
        top={<RfqTable />}
        bottom={
          <TrackingCtxProvider value="bottom panel">
            <MarkPriceLegend />
            <BottomPanel />
          </TrackingCtxProvider>
        }
      />
      <MediaQuery minWidth={MOBILE_BREAKPOINT}>
        <TrackingCtxProvider value="leaderboard">
          <LeaderboardModal />
        </TrackingCtxProvider>
      </MediaQuery>
    </>
  );
}

export function LocalTabFiltersProvider({
  children,
  storageKey,
}: PropsWithChildren<{ storageKey?: string }>) {
  const userId = useSelector(getUserID);
  const [tabsState, setTabsState] = useLocalStorage<TabsState>(
    `${userId}:${storageKey ?? 'unified-rfq-tab-filters'}`,
    INITIAL_TAB_STATE,
  );

  return (
    <RfqFilterProvider tabsState={tabsState} setTabsState={setTabsState}>
      {children}
    </RfqFilterProvider>
  );
}

export function UnifiedRfqProviders({
  children,
  onError = toastWarning,
  onTradeCompleted = toastTrade,
  onTradeSubmitted = toastTradeSubmitted,
  onDrfqClosed = toastDrfqClosed,
  onDrfqOrderCanceled = toastDrfqOrderCanceled,
}: PropsWithChildren<{
  onError?: typeof toastWarning;
  onTradeCompleted?: typeof toastTrade;
  onTradeSubmitted?: typeof toastTradeSubmitted;
  onDrfqClosed?: typeof toastDrfqClosed;
  onDrfqOrderCanceled?: typeof toastDrfqOrderCanceled;
}>) {
  const isUmPlatformStateFeatureFlagEnabled =
    useUnifiedMarketsPlatformStateFeatureFlag();

  const handlers = useMemo(
    () => ({
      onError,
      onTradeCompleted,
      onTradeSubmitted,
      onDrfqClosed,
      onDrfqOrderCanceled,
    }),
    [
      onError,
      onTradeCompleted,
      onTradeSubmitted,
      onDrfqClosed,
      onDrfqOrderCanceled,
    ],
  );
  return (
    <PositionsProviders>
      <LocalTabFiltersProvider>
        <NotificationHandlerProvider>
          <UnifiedRfqServicesProvider handlers={handlers}>
            <InstrumentsProvider>
              <BottomTabsProvider>
                <UMPlatformStateProviders>
                  <GrfqQuoteBuilderModalProvider>
                    <DrfqQuoteBuilderModalProvider>
                      <RfqBuilderModalProvider>
                        {children}
                        {isUmPlatformStateFeatureFlagEnabled && (
                          <PlatformStateHandler
                            position="top-center"
                            usePlatformStateView={useUMPlatformStateView}
                          />
                        )}
                      </RfqBuilderModalProvider>
                    </DrfqQuoteBuilderModalProvider>
                  </GrfqQuoteBuilderModalProvider>
                </UMPlatformStateProviders>
              </BottomTabsProvider>
            </InstrumentsProvider>
          </UnifiedRfqServicesProvider>
        </NotificationHandlerProvider>
      </LocalTabFiltersProvider>
    </PositionsProviders>
  );
}
